import moment from "moment";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AddressInput from "../../common/components/AddressInput";
import Card from "../../common/components/Card";
import CustomForm from "../../common/components/CustomForm";
import DateInput from "../../common/components/DateInput";
import GenderDropdown from "../../common/components/GenderDropdown";
import { Col, Row } from "../../common/components/Grid";
import Label from "../../common/components/Label";
import NumericInput from "../../common/components/NumericInput";
import PhoneInput from "../../common/components/PhoneInput";
import TextInput from "../../common/components/TextInput";
import TextareaInput from "../../common/components/TextareaInput";
import TimeInput from "../../common/components/TimeInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import { useEpcrEditorEpcr, useEpcrEditorIntent } from "./EpcrContext";
import PropTypes from 'prop-types';
import { useCallback, useMemo } from "react";
import { customCtrlTypeSupported } from "../../common/components/DataTable";
import { deepClone } from "../../common/utils/objectUtilities";

const EpcrDetails = ({ disabled = false }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const { epcr, update } = useEpcrEditorEpcr();
    const intent = useEpcrEditorIntent();

    const runUpdate = (field, value) => {
        update(field, value);
    }

    const runURNUpdate = useCallback(delta => {
        Object.keys(delta).forEach(field => update(field, delta[field]));
    }, [update]);

    const updateAge = value => {
        if (value === null || value === undefined) {
            runUpdate("dob", undefined);
        } else if (!epcr.dob) {
            runUpdate('dob', moment().subtract(value, 'year').toDate()) 
        } else {
            let currentAge = moment().diff(epcr.dob, 'year', false);
            let delta = currentAge - value;
            runUpdate('dob', moment(epcr.dob).add(delta, 'year').toDate());
        }
    }

    const def = useMemo(() => {
        if (intent === 'search' && clientConfig.epcrPatientFormSpec) {
            let def = deepClone(clientConfig.epcrPatientFormSpec);
            def.forEach(section => {
                section.rows = section.rows.map(row => row.filter(cell => customCtrlTypeSupported(cell)));
                section.rows = section.rows.filter(row => row.length > 0);
            });
            def = def.filter(section => section.rows.length > 0);
            return def;
        }
        return clientConfig.epcrPatientFormSpec;
    }, [intent, clientConfig.epcrPatientFormSpec]);

    return <Container fluid>
        <Card title={t('patient')}>
            { clientConfig.epcrURNConfig && <CustomForm data={epcr ?? {}} def={clientConfig.epcrURNConfig} disabled={disabled} onChange={runURNUpdate}></CustomForm> }
            <Row>
                <Col md={2}><Label text={`${t('firstname')}:`} hideLabel={true}></Label></Col>
                <Col md={4}><TextInput hideLabel={true} maxLength={256} disabled={disabled} value={epcr?.firstname} onChange={v => runUpdate('firstname', v)} autocomplete="off"></TextInput></Col>
                <Col md={2}><Label text={`${t('middlename')}:`} hideLabel={true}></Label></Col>
                <Col md={4}><TextInput hideLabel={true} maxLength={256} disabled={disabled} value={epcr?.middlename} onChange={v => runUpdate('middlename', v)} autocomplete="off"></TextInput></Col>
            </Row>
            <Row>
                <Col md={2}><Label text={`${t('lastname')}:`} hideLabel={true}></Label></Col>
                <Col md={4}><TextInput hideLabel={true} maxLength={256} disabled={disabled} value={epcr?.lastname} onChange={v => runUpdate('lastname', v)} autocomplete="off"></TextInput></Col>
                <Col md={2}><Label text={`${t('knownas')}:`} hideLabel={true}></Label></Col>
                <Col md={4}><TextInput hideLabel={true} maxLength={256} disabled={disabled} value={epcr?.aka} onChange={v => runUpdate('aka', v)} autocomplete="off"></TextInput></Col>
            </Row>
            <Row>
                <Col md={2}><Label text={`${t('dob')}:`} hideLabel={true}></Label></Col>
                <Col md={4}><DateInput hideLabel={true} disabled={disabled} value={epcr?.dob} tz="UTC" onChange={v => runUpdate('dob', v)} placeholder={t('dob')}></DateInput></Col>
                <Col md={2}><Label text={`${t('age')}:`} hideLabel={true}></Label></Col>
                <Col md={4}><NumericInput hideLabel={true} maxLength={256} disabled={disabled} value={epcr?.age} onChange={updateAge} autocomplete="off"></NumericInput></Col>
            </Row>
            <Row>
                <Col md={2}><Label text={`${t('gender')}:`} hideLabel={true}></Label></Col>
                <Col md={4}><GenderDropdown hideLabel={true} disabled={disabled} gender={epcr?.gender} onChange={v => runUpdate('gender', v)}></GenderDropdown></Col>
                <Col md={2}><Label text={`${t('weight')} (kg):`} hideLabel={true}></Label></Col>
                <Col md={4}><NumericInput hideLabel={true} disabled={disabled} value={epcr?.weight} onChange={v => runUpdate('weight', v)} live={false} autocomplete="off"></NumericInput></Col>
            </Row>
        </Card>
        <Card title={t('patientAddress')}>
            <Row>
                <Col md={2}><Label text={`${t('address')}:`} hideLabel={true}></Label></Col>
                <Col md={10}>
                    {clientConfig.legecyEpcrAddress ? 
                    <TextInput hideLabel={true} maxLength={512} disabled={disabled} value={epcr?.address} onChange={a => runUpdate('address', a)} autocomplete="off"></TextInput>
                    :
                    <AddressInput disabled={disabled} address={epcr} onChange={runUpdate} autocomplete="off"></AddressInput>
                    }
                </Col>
            </Row>
            <Row>
                <Col md={2}><Label text={`${t('phone')}:`} hideLabel={true}></Label></Col>
                <Col md={10}><PhoneInput hideLabel={true} maxLength={20} disabled={disabled} value={epcr?.phone} onChange={p => runUpdate('phone', p)} autocomplete="off"></PhoneInput></Col>
            </Row>
        </Card>
        {clientConfig.epcrPatientFormSpec ? <CustomForm disabled={disabled} hideLabel={true} def={def} data={epcr?.customfields ?? {}} onChange={v => runUpdate('customfields', v)}></CustomForm> : null}
        <Card title={t('comment', {count: 100})}>
            <TextareaInput hideLabel={true} disabled={disabled} rows={8} maxLength={1024} value={epcr?.comments} onChange={c => runUpdate('comments', c)} autocomplete="off"></TextareaInput>
        </Card>
        <Card title={t('recordDetails')}>
            { clientConfig.epcrEnableEventID && <Row>
                <Col md={1}><Label text={t('epcrEventID')} hideLabel={true}></Label></Col>
                <Col md={5}><TextInput value={epcr?.eventid} maxLength={128} hideLabel={true} disabled={disabled} onChange={v => runUpdate('eventid', v)} placeholder={t('epcrEventID')}></TextInput></Col>
            </Row> }
            <Row>
                <Col md={1}><Label text={`${t('dateTime')}:`} hideLabel={true}></Label></Col>
                <Col md={5}><DateInput hideLabel={true} value={epcr?.creation} disabled={disabled} onChange={v => runUpdate('creation', v)} placeholder={t('dateTime')}></DateInput></Col>
                <Col md={1}><Label text={`${t('timeOfDay')}:`} hideLabel={true}></Label></Col>
                <Col md={5}><TimeInput hideLabel={true} value={epcr?.creation} disabled={disabled} onChange={v => runUpdate('creation', v)} placeholder={t('timeOfDay')}></TimeInput></Col>
            </Row>
        </Card>
    </Container>
};

EpcrDetails.propTypes = {
    disabled: PropTypes.bool
}

export default EpcrDetails;